import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Empty,
  Spin,
  Card,
  Tag,
  Button,
  Breadcrumb,
  message,
  Modal,
  Image,
  Divider,
  Tooltip,
} from 'antd';
import {
  ShoppingCartOutlined,
  HeartOutlined,
  AppstoreOutlined,
  BarsOutlined,
  EyeOutlined,
  HomeOutlined,
} from '@ant-design/icons';
import {
  productService,
  ProductItem,
  ProductListResponse,
} from '../../services/product.service';
import { categoryService, Category } from '../../services/category.service';
import { useCart } from '../../context/CartContext';
import ProductCard from '../../components/products/ProductCard';
import { showSuccess } from '../../utils/notificationUtils';
import {
  DEFAULT_PRODUCT_IMAGE,
  getProductImageUrl,
  handleProductImageError,
} from '../../utils/productImage';
// import { productService } from '../../services/product.service';

const { Meta } = Card;

const CategoryPage: React.FC = () => {
  const { categorySlug } = useParams<{ categorySlug: string }>();
  // console.log('categorySlug', categorySlug);
  // State
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [categoryLoading, setCategoryLoading] = useState<boolean>(true);
  const [category, setCategory] = useState<Category | null>(null);
  const [total, setTotal] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(12);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [quickViewProduct, setQuickViewProduct] = useState<ProductItem | null>(
    null
  );
  const [quickViewVisible, setQuickViewVisible] = useState(false);

  // Ref for intersection observer
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const cart = useCart();

  // Fetch products based on category
  const fetchProducts = useCallback(
    async (page: number, append: boolean = false) => {
      if (!categorySlug) return;

      if (page === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      try {
        const params = {
          page,
          limit: pageSize,
          categoryslug: categorySlug,
        };

        const response: ProductListResponse =
          await productService.getAllProducts(params);

        if (append) {
          setProducts((prev) => [...prev, ...response.products]);
        } else {
          setProducts(response.products);
        }

        setTotal(response.total);
        // Update hasMore based on current page and total pages
        setHasMore(page < response.totalPages);
      } catch (error) {
        console.error('Failed to fetch products:', error);
        message.error('Không thể tải sản phẩm');
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [categorySlug, pageSize] // Remove products.length from dependencies
  );

  // Initial load of products
  useEffect(() => {
    setCurrentPage(1);
    fetchProducts(1, false);
  }, [categorySlug, fetchProducts]);

  // Load more products when scrolling
  const loadMoreProducts = useCallback(() => {
    if (loadingMore || !hasMore) return;
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchProducts(nextPage, true);
  }, [currentPage, fetchProducts, hasMore, loadingMore]);

  // Setup intersection observer for infinite scroll
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '100px', // Add some margin to trigger earlier
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasMore && !loadingMore && !loading) {
        loadMoreProducts();
      }
    }, options);

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [hasMore, loadMoreProducts, loadingMore, loading]);

  // Fetch category info
  useEffect(() => {
    const fetchCategoryInfo = async () => {
      if (!categorySlug) return;

      setCategoryLoading(true);
      try {
        const categories = await categoryService.getPublicCategories();
        const categoryInfo = categories.find((c) => c.slug === categorySlug);
        if (categoryInfo) {
          setCategory(categoryInfo);
          document.title = `${categoryInfo.name} - Fruit Shop`;
        } else {
          message.error('Không tìm thấy danh mục');
        }
      } catch (error) {
        console.error('Failed to fetch category info:', error);
        message.error('Không thể tải thông tin danh mục');
      } finally {
        setCategoryLoading(false);
      }
    };

    fetchCategoryInfo();
  }, [categorySlug, fetchProducts]);

  const handleAddToCart = (product: ProductItem) => {
    cart.addItem(
      {
        id: product.id,
        productId: product.id,
        name: product.name,
        price: product.current_price,
        originalPrice: product.original_price,
        image: getProductImageUrl(product.images?.[0]),
        quantity: 1,
        weight: product.unit || '',
        unit: product.unit,
        stock: 10, // Placeholder, would come from actual API
        type: 'single',
      },
      1
    );

    // Show success notification
    showSuccess(
      'Đã thêm vào giỏ hàng',
      `${product.name} đã được thêm vào giỏ hàng của bạn.`
    );
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const openQuickView = (product: ProductItem) => {
    setQuickViewProduct(product);
    setQuickViewVisible(true);
  };

  const closeQuickView = () => {
    setQuickViewVisible(false);
  };

  const addToCartFromQuickView = () => {
    if (quickViewProduct) {
      handleAddToCart(quickViewProduct);
      closeQuickView();
    }
  };

  return (
    <div className='container mx-auto px-4 py-8'>
      {/* Breadcrumb */}
      <Breadcrumb
        className='p-3 bg-white shadow-sm rounded-lg border border-gray-100 mb-4'
        separator={<span className='text-gray-400 mx-1'>/</span>}
        items={[
          {
            title: (
              <Link
                to='/'
                className='flex items-center text-gray-600 hover:text-primary transition-colors'
              >
                <HomeOutlined className='mr-1' />
                <span>Trang chủ</span>
              </Link>
            ),
          },
          {
            title: (
              <Link
                to='/products'
                className='text-gray-600 hover:text-primary transition-colors'
              >
                Sản phẩm
              </Link>
            ),
          },
          {
            title: (
              <span className='font-medium text-primary'>
                {categoryLoading ? 'Đang tải...' : category?.name || 'Danh mục'}
              </span>
            ),
          },
        ]}
      />

      <div className='mb-6'>
        <h1 className='text-2xl font-bold text-primary relative inline-block'>
          {category?.name || 'Đang tải...'}
          <span className='absolute -bottom-1 left-0 right-0 h-1 bg-primary/20 rounded-full'></span>
        </h1>
        {category?.description && (
          <p className='text-gray-600 mt-4'>{category.description}</p>
        )}
      </div>

      {/* Products display area */}
      <div className='mb-4 flex justify-between items-center'>
        <div>
          <p className='text-gray-500'>
            {loading && currentPage === 1
              ? 'Đang tải sản phẩm...'
              : `Hiển thị ${products.length} trong số ${total} sản phẩm`}
          </p>
        </div>

        <div className='flex items-center space-x-2'>
          <span className='text-gray-500 text-sm hidden md:inline'>
            Hiển thị:
          </span>
          <Button
            type={viewMode === 'grid' ? 'primary' : 'default'}
            icon={<AppstoreOutlined />}
            onClick={() => setViewMode('grid')}
            size='small'
          />
          <Button
            type={viewMode === 'list' ? 'primary' : 'default'}
            icon={<BarsOutlined />}
            onClick={() => setViewMode('list')}
            size='small'
          />
        </div>
      </div>

      {/* Products display */}
      {loading && currentPage === 1 ? (
        <div className='flex justify-center py-12'>
          <Spin size='large' />
        </div>
      ) : products.length === 0 ? (
        <Empty
          description='Không tìm thấy sản phẩm trong danh mục này'
          className='py-12'
        />
      ) : (
        <>
          {viewMode === 'grid' ? (
            <div className='grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-3'>
              {products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          ) : (
            <div className='space-y-4'>
              {products.map((product) => (
                <div
                  key={product.id}
                  className='group relative transition-all duration-300 border border-gray-100 hover:border-primary/30 rounded-lg hover:shadow-lg bg-white overflow-hidden'
                >
                  <div className='flex flex-col md:flex-row'>
                    {/* Badges */}
                    <div className='absolute top-2 left-2 z-10 flex flex-col gap-1'>
                      {product.is_on_sale && (
                        <span className='bg-red-500 text-white text-xs font-bold px-2 py-1 rounded'>
                          SALE
                        </span>
                      )}
                    </div>

                    {/* Trending badge */}
                    {product.is_trending && (
                      <div className='absolute top-2 right-2 z-10'>
                        <span className='bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded'>
                          HOT
                        </span>
                      </div>
                    )}

                    {/* Product image */}
                    <div className='md:w-1/4 relative overflow-hidden'>
                      <Link to={`/products/${product.id}`}>
                        <div className='overflow-hidden h-[200px] flex items-center justify-center p-4'>
                          <img
                            alt={product.name}
                            src={getProductImageUrl(product.images?.[0])}
                            className='w-full h-full object-contain transition-transform duration-500 group-hover:scale-105'
                            onError={handleProductImageError}
                          />
                        </div>
                      </Link>
                    </div>

                    {/* Product details */}
                    <div className='md:w-3/4 p-4 flex flex-col'>
                      <Link to={`/products/${product.id}`}>
                        <h3 className='text-lg font-semibold group-hover:text-primary transition-colors'>
                          {product.name}
                        </h3>
                      </Link>

                      <p className='text-gray-600 text-sm line-clamp-2 my-2'>
                        {product.description}
                      </p>

                      <div className='mt-2 mb-3'>
                        <span className='text-red-500 font-semibold text-lg'>
                          {formatPrice(product.current_price)}
                        </span>
                        {product.is_on_sale &&
                          product.original_price > product.current_price && (
                            <span className='text-gray-400 line-through text-sm ml-2'>
                              {formatPrice(product.original_price)}
                            </span>
                          )}
                      </div>

                      <div className='mt-auto pt-3 flex space-x-2'>
                        <Button
                          type='primary'
                          icon={<ShoppingCartOutlined />}
                          onClick={() => handleAddToCart(product)}
                          className='transition-all hover:bg-primary/90 shadow'
                        >
                          Thêm vào giỏ
                        </Button>

                        <Tooltip title='Xem nhanh'>
                          <Button
                            icon={<EyeOutlined />}
                            onClick={() => openQuickView(product)}
                            className='hover:text-primary hover:border-primary'
                          />
                        </Tooltip>

                        <Tooltip title='Yêu thích'>
                          <Button
                            icon={<HeartOutlined />}
                            className='hover:text-primary hover:border-primary'
                          />
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Load more indicator */}
          <div ref={loadMoreRef} className='mt-8 py-4 text-center'>
            {loadingMore && (
              <div className='flex justify-center'>
                <Spin size='default' />
                <span className='ml-2 text-gray-500'>
                  Đang tải thêm sản phẩm...
                </span>
              </div>
            )}
            {!hasMore && products.length > 0 && (
              <p className='text-gray-500'>Đã hiển thị tất cả sản phẩm</p>
            )}
          </div>
        </>
      )}

      {/* Quick View Modal - giống như trong AllProducts */}
      <Modal
        open={quickViewVisible}
        onCancel={closeQuickView}
        footer={null}
        width={800}
        title={null}
        centered
        className='quick-view-modal'
      >
        {quickViewProduct && (
          <div className='flex flex-col md:flex-row gap-6'>
            <div className='md:w-1/2'>
              <Image
                src={getProductImageUrl(quickViewProduct.images?.[0])}
                alt={quickViewProduct.name}
                className='object-contain'
                preview={false}
                fallback={DEFAULT_PRODUCT_IMAGE}
              />
            </div>
            <div className='md:w-1/2 flex flex-col'>
              <h2 className='text-xl font-semibold'>{quickViewProduct.name}</h2>

              <div className='mt-2 mb-4'>
                <span className='text-red-500 font-semibold text-xl'>
                  {formatPrice(quickViewProduct.current_price)}
                </span>
                {quickViewProduct.is_on_sale &&
                  quickViewProduct.original_price >
                    quickViewProduct.current_price && (
                    <span className='text-gray-400 line-through text-sm ml-2'>
                      {formatPrice(quickViewProduct.original_price)}
                    </span>
                  )}
              </div>

              <div className='mb-4'>
                <p className='text-gray-700 line-clamp-4'>
                  {quickViewProduct.description}
                </p>
              </div>

              <div className='mt-2 space-x-2'>
                {quickViewProduct.is_trending && (
                  <Tag color='orange'>Trending</Tag>
                )}
                {quickViewProduct.is_on_sale && <Tag color='red'>Sale</Tag>}
              </div>

              <Divider />

              <div className='mt-auto space-y-3'>
                <Button
                  type='primary'
                  icon={<ShoppingCartOutlined />}
                  onClick={addToCartFromQuickView}
                  block
                  size='large'
                >
                  Thêm vào giỏ hàng
                </Button>

                <Link to={`/products/${quickViewProduct.id}`}>
                  <Button block size='large'>
                    Xem chi tiết
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CategoryPage;
